import { Check, Copy } from 'lucide-react';
import { truncateMiddle } from '../lib/format';
import { useCopy } from './useCopy';

type HashProps = {
  value: string | null | undefined;
  /** Characters kept at each end when truncated. */
  head?: number;
  tail?: number;
  /** Show the whole value, wrapping. */
  full?: boolean;
  /** Link the value (e.g. to a block explorer). */
  href?: string;
  /** What this is, for the copy button's label ("transaction id"). */
  label?: string;
  muted?: boolean;
  copyable?: boolean;
  className?: string;
};

/**
 * A hash, txid, public key or path in monospace. Long values are
 * middle-truncated (both ends matter when comparing hashes by eye); the full
 * value is in the title and one click copies it.
 */
export function Hash({ value, head = 8, tail = 6, full, href, label = 'value', muted, copyable = true, className }: HashProps) {
  const { copied, copy } = useCopy();
  if (!value) return <span className="subtle">—</span>;
  const shown = full ? value : truncateMiddle(value, head, tail);
  const cls = ['ui-hash', full && 'ui-hash--full', muted && 'ui-hash--muted', className].filter(Boolean).join(' ');

  return (
    <span className={cls}>
      {href ? (
        <a className="ui-hash__value" href={href} target="_blank" rel="noreferrer noopener" title={value}>
          {shown}
        </a>
      ) : (
        <span className="ui-hash__value" title={value}>
          {shown}
        </span>
      )}
      {copyable && (
        <button
          type="button"
          className="ui-copy"
          data-copied={copied}
          onClick={(e) => {
            e.stopPropagation();
            void copy(value);
          }}
          aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
          title={copied ? 'Copied' : `Copy ${label}`}
        >
          {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
        </button>
      )}
      <span className="sr-only" aria-live="polite">
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </span>
  );
}
